import { Button } from "@groot/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { Page } from "@/app/shared/components/page";
import { PageHeader } from "@groot/shell/components/layout";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { Textarea } from "@groot/ui/textarea";
import { useConfirm } from "@groot/ui/primitives";
import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import type { CreditReport } from "@/types";
import dayjs from "dayjs";
import { Eye, Plus, Trash2, TrendingUp, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function CreditReports() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [creditReports, setCreditReports] = useState<CreditReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Form state
  const [bureau, setBureau] = useState<"cibil" | "experian" | "crif">("cibil");
  const [reportDate, setReportDate] = useState("");
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [creditScore, setCreditScore] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getCreditReports({ includeDeleted: false });
      setCreditReports(data);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load credit reports",
      });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      setPdfFile(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!pdfFile || !bureau || !reportDate) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please fill in all required fields",
      });
      return;
    }

    try {
      setUploading(true);
      await apiClient.uploadCreditReport({
        bureau,
        reportDate,
        pdf: pdfFile,
        creditScore: creditScore ? Number.parseInt(creditScore) : undefined,
        notes,
      });

      toast({
        title: "Success",
        description: "Credit report uploaded successfully",
      });

      // Reset form
      setShowUploadForm(false);
      setBureau("cibil");
      setReportDate("");
      setPdfFile(null);
      setCreditScore("");
      setNotes("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      await loadData();
    } catch (err) {
      console.error(err);
      toast({
        variant: "destructive",
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to upload credit report",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleView = (id: number) => {
    navigate(`/credit-reports/${id}`);
  };

  const handleDelete = async (id: number) => {
    if (
      !(await confirm({
        title: "Confirm Action",
        description: "Are you sure you want to delete this credit report?",
      }))
    ) {
      return;
    }

    try {
      await apiClient.deleteCreditReport(id);
      toast({
        title: "Success",
        description: "Credit report deleted successfully",
      });
      await loadData();
    } catch (err) {
      console.error(err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete credit report",
      });
    }
  };

  const formatDate = (date: string) => {
    return dayjs(date).format("MMM DD, YYYY");
  };

  const formatBureau = (bureau: string) => {
    return bureau.toUpperCase();
  };

  const getBureauColor = (bureau: string) => {
    switch (bureau) {
      case "cibil":
        return "text-info";
      case "experian":
        return "text-chart-6";
      case "crif":
        return "text-success";
      default:
        return "text-muted-foreground";
    }
  };

  if (loading) {
    return (
      <Page>
        <LoadingSpinner size="lg" className="py-20" />
      </Page>
    );
  }

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title="Credit Reports"
        description="Track your credit score over time"
        className="mb-8"
        actions={
          <Button onClick={() => setShowUploadForm(!showUploadForm)}>
            {showUploadForm ? (
              <>Cancel</>
            ) : (
              <>
                <Plus className="size-4" />
                Upload Report
              </>
            )}
          </Button>
        }
      />

      <div className="space-y-6">
        {showUploadForm && (
          <div className="border border-border rounded-lg p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="bureau">Credit Bureau *</Label>
                <Select
                  value={bureau}
                  onValueChange={(value) => setBureau(value as "cibil" | "experian" | "crif")}
                >
                  <SelectTrigger id="bureau">
                    <SelectValue placeholder="Select bureau" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cibil">CIBIL</SelectItem>
                    <SelectItem value="experian">Experian</SelectItem>
                    <SelectItem value="crif">CRIF</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="reportDate">Report Date *</Label>
                <Input
                  id="reportDate"
                  type="date"
                  value={reportDate}
                  onChange={(e) => setReportDate(e.target.value)}
                  required
                />
              </div>

              <div>
                <Label htmlFor="creditScore">Credit Score</Label>
                <Input
                  id="creditScore"
                  type="number"
                  min="300"
                  max="900"
                  value={creditScore}
                  onChange={(e) => setCreditScore(e.target.value)}
                  placeholder="e.g., 750"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Optional: Enter your credit score (300-900)
                </p>
              </div>

              <div>
                <Label htmlFor="pdf">PDF File *</Label>
                <Input
                  id="pdf"
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileChange}
                  required
                />
                {pdfFile && (
                  <p className="text-sm text-muted-foreground mt-1">Selected: {pdfFile.name}</p>
                )}
              </div>

              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional notes about this report"
                  rows={3}
                />
              </div>

              <Button type="submit" disabled={uploading} className="w-full">
                {uploading ? (
                  <>
                    <Upload className="size-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="size-4" />
                    Upload Credit Report
                  </>
                )}
              </Button>
            </form>
          </div>
        )}

        <div className="border border-border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bureau</TableHead>
                <TableHead>Report date</TableHead>
                <TableHead className="text-center">Credit score</TableHead>
                <TableHead>File</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {creditReports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="px-4 py-12 text-center text-muted-foreground">
                    <TrendingUp className="mx-auto size-12 mb-3 opacity-20" />
                    <p className="text-sm">No credit reports found</p>
                    <p className="text-xs mt-1">
                      Upload your first credit report to track your score
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                creditReports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell>
                      <span className={`text-sm font-semibold ${getBureauColor(report.bureau)}`}>
                        {formatBureau(report.bureau)}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium">{formatDate(report.reportDate)}</TableCell>
                    <TableCell className="text-center">
                      {report.creditScore ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-primary/10 text-primary">
                          {report.creditScore}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{report.fileName}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleView(report.id)}>
                          <Eye className="size-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(report.id)}>
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </Page>
  );
}
